import Link from 'next/link';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import { getRouteUrl } from '@utils/urls';

import { storeWithDistanceShape } from '@common/types/shops';
import { STORE } from '@common/routes';
import Pill from '@common/components/Pill';
import { SIZES, VARIANTS } from '@common/constants/pills';

import Sizes from './Sizes';
import Status from './Status';
import {
  ChevronIcon,
  CityWrapper,
  Title,
  SubTitle,
  Wrapper,
  TitlesWrapper,
  DistanceValue,
} from './StoreCard.styled';

const StoreCard = ({
  store: {
    city,
    isOutlet,
    additionalLocationInfo,
    houseNr,
    street,
    zipCode,
    businessHours,
    slug,
    id,
    distance,
  },
  sizes,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const withSizes = Array.isArray(sizes);

  return (
    <Link
      legacyBehavior
      href={{
        pathname: STORE,
        query: { slug, store: id },
      }}
      as={getRouteUrl(language, STORE, { slug, store: id })}
      passHref
    >
      <Wrapper withSizes={withSizes}>
        <TitlesWrapper withSizes={withSizes}>
          {typeof distance !== 'undefined' && (
            <DistanceValue data-testid="store-card-distance">
              {distance > 1
                ? `${distance.toFixed(1)} ${t('storefinder.storeCard.kilometer', 'Kilometer')}`
                : `${Math.round(distance * 1000)} ${t('storefinder.storeCard.meter', 'Meter')}`}
            </DistanceValue>
          )}
          <CityWrapper>
            <Title>{city}</Title>
            {isOutlet && (
              <Pill
                size={SIZES.SMALL}
                variant={VARIANTS.LIGHT}
              >
                {t('storefinder.storeCard.outlet', 'Outlet')}
              </Pill>
            )}
          </CityWrapper>
          {additionalLocationInfo && (
            <SubTitle>{additionalLocationInfo}</SubTitle>
          )}
          <SubTitle>{`${street} ${houseNr}, ${zipCode} ${city}`}</SubTitle>
          <ChevronIcon />
        </TitlesWrapper>
        {withSizes && <Sizes sizes={sizes} />}
        <Status businessHours={businessHours} />
      </Wrapper>
    </Link>
  );
};

StoreCard.propTypes = {
  store: storeWithDistanceShape.isRequired,
  sizes: PropTypes.arrayOf(PropTypes.string),
};

StoreCard.defaultProps = {
  sizes: undefined,
};

export default StoreCard;
